/**
 * Post-process Shopify-stored article HTML before rendering.
 *
 * Field-notes articles are often pasted from ChatGPT / ProseMirror editors,
 * which leaves three problems that hurt readability:
 *
 *   1. Inline citation links rendered as parenthesised full domain names,
 *      e.g.  "(sciencedirect.com)" inline in body copy, breaking the line
 *      rhythm and making the page look noisy.
 *   2. Editor metadata pollution: `data-start`, `data-end`, `data-col-size`,
 *      `data-pm-slice`, stray `<meta charset>` tags inside the body, and
 *      hard-coded inline `style="width: NN%"` on every <td>/<th>.
 *   3. No proper sources list at the end of the article.
 *
 * This helper:
 *   - Strips editor metadata and inline table widths.
 *   - Replaces ChatGPT-style citation links with compact numbered
 *     superscript references, deduplicated by URL.
 *   - Appends a "Sources" ordered list to the article when any citations
 *     were collected.
 *
 * Runs before sanitizeHtml so DOMPurify still strips anything unsafe.
 */
export function formatArticleHtml(html: string): string {
  if (!html) return "";

  let out = html;

  // 1. Strip ProseMirror / ChatGPT editor metadata attributes everywhere.
  out = out.replace(/\s+data-(?:start|end|col-size|pm-slice|is-last-node|placeholder|node-view-content-react|node-view-content)="[^"]*"/g, "");

  // 2. Remove stray <meta> tags that ended up inside the body content.
  out = out.replace(/<meta\b[^>]*>/gi, "");

  // 3. Strip hard-coded inline width styles on table cells/rows so the
  //    responsive CSS can take over.
  out = out.replace(/(<(?:td|th|tr|table|colgroup|col)\b[^>]*?)\sstyle="[^"]*"/gi, "$1");

  // 4. Remove `disabled` attribute that ChatGPT pastes onto <a> tags
  //    (invalid HTML on anchors and prevents clicks in some browsers).
  out = out.replace(/(<a\b[^>]*?)\sdisabled(?=[\s>])/gi, "$1");

  // 5. Collect ChatGPT-style citation links and replace with numbered
  //    superscript references.
  const sources: { url: string; label: string }[] = [];
  const urlToIndex = new Map<string, number>();

  const registerCitation = (rawUrl: string, rawLabel: string): number => {
    // Strip the chatgpt utm tracker so the published link stays clean.
    const cleanUrl = rawUrl
      .replace(/[?&]utm_source=chatgpt\.com\b/gi, "")
      .replace(/\?&/, "?")
      .replace(/\?$/, "");
    const key = cleanUrl;
    const existing = urlToIndex.get(key);
    if (existing !== undefined) return existing;
    const label = rawLabel.replace(/<[^>]+>/g, "").trim() || cleanUrl;
    const idx = sources.length + 1;
    urlToIndex.set(key, idx);
    sources.push({ url: cleanUrl, label });
    return idx;
  };

  // 5a. Pattern: "(<a ...?utm_source=chatgpt.com">domain</a>)" — with the
  //     parens often wrapped in <span> tags by the editor.
  out = out.replace(
    /(?:<span[^>]*>)?\s*\(\s*(?:<\/span>)?\s*<a\b([^>]*?)href="([^"]*utm_source=chatgpt\.com[^"]*)"([^>]*)>([\s\S]*?)<\/a>\s*(?:<span[^>]*>)?\s*\)\s*(?:<\/span>)?/gi,
    (_m, _pre, url, _post, inner) => {
      const idx = registerCitation(url, inner);
      const clean = url.replace(/[?&]utm_source=chatgpt\.com\b/gi, "").replace(/\?&/, "?").replace(/\?$/, "");
      return `<sup class="citation"><a href="${clean}" target="_blank" rel="noopener nofollow">[${idx}]</a></sup>`;
    },
  );

  // 5b. Bare chatgpt-tagged links not wrapped in parens — still tag them.
  out = out.replace(
    /<a\b([^>]*?)href="([^"]*utm_source=chatgpt\.com[^"]*)"([^>]*)>([\s\S]*?)<\/a>/gi,
    (_m, _pre, url, _post, inner) => {
      const idx = registerCitation(url, inner);
      const clean = url.replace(/[?&]utm_source=chatgpt\.com\b/gi, "").replace(/\?&/, "?").replace(/\?$/, "");
      return `<sup class="citation"><a href="${clean}" target="_blank" rel="noopener nofollow">[${idx}]</a></sup>`;
    },
  );

  // 6. Clean up empty <span> wrappers left over from the editor.
  out = out.replace(/<span>\s*<\/span>/g, "");
  // Collapse runs of whitespace introduced by removed attributes.
  out = out.replace(/[ \t]{2,}/g, " ");

  // 7. Append the Sources section if we collected anything.
  if (sources.length > 0) {
    const items = sources
      .map(
        (s) =>
          `<li><a href="${s.url}" target="_blank" rel="noopener nofollow">${escapeText(s.label)}</a></li>`,
      )
      .join("");
    out += `<section class="article-sources" aria-label="Sources"><h2>Sources</h2><ol>${items}</ol></section>`;
  }

  return out;
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
